import { httpGet } from 'utils';

/**
 * Return valuation data for a specific portfolio and date
 */
export interface ReturnValuationData {
  acctHolderId: number;
  acctHolderName: string;
  portfolioId: string;
  portfolioName: string;
  valuationDate: string;
  valuationStart: number | null;
  valuationEnd: number | null;
  purchased: number | null;
  sold: number | null;
  cash: number | null;
  fees: number | null;
  paidIn: number | null;
  refund: number | null;
  transferOut: number | null;
  transferIn: number | null;
  broking: number | null;
  cumCash: number | null;
  nav: number | null;
  inFlow: number;
  inflowAccum: number;
  outFlow: number;
  outflowAccum: number;
  netCapitalInvested: number;
  cumNetCapitalInvested: number;
  netPosition: number | null;
  totalNetPositionPercent: number | null;
  rm: string | null;
  netPositionPrev: number | null;
  rank: number;
  oneMonthHoldingChange: number;
  oneMonthHoldingReturn: number | null;
  oneMonthNetPositionChange: number | null;
  oneMonthNetPositionReturn: number | null;
}

export interface ReturnValuationsResponse {
  data: ReturnValuationData[];
  totalRecords: number;
  totalResults: number;
}

interface FetchReturnValuationsParams {
  /** Account holder ID (required, obtained from PortfolioStatements) */
  acctHolderId: number;
  /** Portfolio ID - omit or pass empty/null for all portfolios */
  portfolioId?: string | null;
  /** Specific valuation date in YYYY-MM-DD format */
  valuationDate?: string;
  /** Page number for pagination (default 0) */
  page?: number;
  /** Number of records per page (default 20) */
  pageSize?: number;
}

/**
 * Fetch return valuations for the logged-in user's portfolio(s)
 *
 * @param params - Query parameters for the API call (acctHolderId is required)
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise with return valuations data
 *
 * @example
 * // Get all portfolios
 * const allValuations = await fetchReturnValuations({
 *   acctHolderId: 2444,
 *   valuationDate: '2022-11-30',
 * });
 *
 * // Get specific portfolio
 * const portfolioValuations = await fetchReturnValuations({
 *   acctHolderId: 2444,
 *   portfolioId: '14435',
 *   valuationDate: '2022-11-30',
 *   page: 0,
 *   pageSize: 10
 * });
 */
export const fetchReturnValuations = (params: FetchReturnValuationsParams, signal?: AbortSignal) => {
  const queryParams: Record<string, string | number> = {
    docType: 'return_valuations',
    acctHolderId: params.acctHolderId,
    page: params.page ?? 0,
    pageSize: params.pageSize ?? 20,
  };

  // Only add portfolioId if it's a non-empty value (not null, undefined, empty string, or 'all')
  if (params.portfolioId && params.portfolioId !== 'all' && params.portfolioId.trim() !== '') {
    queryParams.portfolioId = params.portfolioId;
  }

  if (params.valuationDate) {
    queryParams.valuationDate = params.valuationDate;
  }

  return httpGet<ReturnValuationsResponse, Record<string, string | number>>(
    'portal/PortalDocument',
    queryParams,
    true,
    signal,
  );
};
